import { useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import type { Announcement } from "./AnnouncementBanner.js";

const TYPE_LABEL: Record<string, string> = {
  update: "アップデート",
  maintenance: "メンテナンス",
  incident: "障害",
  news: "お知らせ",
};

/**
 * ヘッダー右上のお知らせベル。
 * - `notifyBell=true` かつ未読が 1 件以上 → 赤点表示
 * - クリックでドロップダウン表示
 * - 「既読にする」で該当お知らせを既読化
 */
export function NotificationBell() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = async () => {
    try {
      const r = await api.getAnnouncements();
      setItems(r.announcements || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    // 外クリックで閉じる
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const bellItems = items.filter((a) => a.notifyBell);
  const unreadCount = bellItems.filter((a) => !a.isRead).length;

  const handleMarkRead = async (id: string) => {
    try {
      await api.markAnnouncementRead(id);
      setItems((prev) => prev.map((a) => (a.id === id ? { ...a, isRead: true } : a)));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    const unread = bellItems.filter((a) => !a.isRead);
    await Promise.all(unread.map((a) => api.markAnnouncementRead(a.id).catch(() => {})));
    setItems((prev) => prev.map((a) => (a.notifyBell ? { ...a, isRead: true } : a)));
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 6,
          position: "relative",
          fontSize: 20,
          lineHeight: 1,
        }}
        aria-label="お知らせ"
        title="お知らせ"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 9,
              height: 9,
              borderRadius: "50%",
              background: "#d32f2f",
              border: "2px solid white",
            }}
          />
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 360,
            maxHeight: 480,
            overflow: "auto",
            background: "white",
            border: "1px solid #e8e7e3",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #e8e7e3",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 14 }}>お知らせ</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#534AB7",
                  fontSize: 12,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                すべて既読にする
              </button>
            )}
          </div>
          {bellItems.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#6b6b68", fontSize: 13 }}>
              新しいお知らせはありません
            </div>
          ) : (
            bellItems.map((a) => (
              <div
                key={a.id}
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #f0efeb",
                  background: a.isRead ? "white" : "#FAF8FF",
                  position: "relative",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  {!a.isRead && (
                    <span
                      style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", flexShrink: 0 }}
                    />
                  )}
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: a.isImportant ? "#C62828" : "#6b6b68",
                    }}
                  >
                    {a.isImportant && "重要・"}
                    {TYPE_LABEL[a.type]}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{a.title}</div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b6b68",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    marginBottom: 6,
                  }}
                >
                  {a.body.length > 120 ? a.body.slice(0, 120) + "…" : a.body}
                </div>
                {!a.isRead && (
                  <button
                    onClick={() => handleMarkRead(a.id)}
                    style={{
                      background: "transparent",
                      border: "1px solid #e8e7e3",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                      color: "#534AB7",
                    }}
                  >
                    既読にする
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
